<template>
  <div class="docs-preview-item">
    <div class="placeholder-results">
      <p @click="toggleKycAccordion">
        <i class="bi bi-file-earmark-text-fill"></i> Solicitar documento KYC BS2
        <span class="float-end text-end">
          <i v-if="!isOpenedKyc" class="bi bi-arrow-down-square-fill"></i>
          <i v-else class="bi bi-arrow-up-square-fill"></i>
        </span>
      </p>

      <div class="collapse" ref="collapseKycPreview">
        <div class="row">
          <form-select
            class="col-12 form-input"
            label="Propósito da relação de negócios"
            v-model="kycBusinessPurposeValue"
            :items="kycBusinessPurposeOptions"
            id="kycBusinessPurpose"
            name="kycBusinessPurpose"
            :isDisabled="!hasInputAllowed()"
          />

          <form-input
            className="col-12 form-input"
            label="Finalidade das remessas"
            v-model="remittancePurposeValue"
            id="remittancePurpose"
            name="remittancePurpose"
            type="text"
            :isDisabled="!hasInputAllowed()"
          />

          <form-select
            class="col-12 form-input"
            label="Nível operacional pretendido"
            v-model="kycOperationalLevelValue"
            :items="kycOperationalLevelOptions"
            id="kycOperationalLevel"
            name="kycOperationalLevel"
            :isDisabled="!hasInputAllowed()"
          />

          <form-input
            className="col-12 form-input"
            label="Contraparte no exterior"
            v-model="foreignCounterpartyNameModel"
            id="kycForeignCounterpartyName"
            name="kycForeignCounterpartyName"
            type="text"
            :isDisabled="!hasInputAllowed()"
          />

          <form-select
            class="col-12 form-input"
            label="País da contraparte no exterior"
            v-model="foreignCounterpartyCountryModel"
            :items="countries"
            id="kycForeignCounterpartyCountry"
            name="kycForeignCounterpartyCountry"
            :isDisabled="!hasInputAllowed()"
          />

          <form-input
            className="col-12 form-input"
            label="Como o cliente foi captado?"
            v-model="kycHowClientCapturedValue"
            id="kycHowClientCaptured"
            name="kycHowClientCaptured"
            type="text"
            :isDisabled="!hasInputAllowed()"
          />

          <form-input
            className="col-12 form-input"
            label="Qual o tempo de relacionamento com o cliente?"
            v-model="kycRelationshipTimeValue"
            id="kycRelationshipTime"
            name="kycRelationshipTime"
            type="text"
            :isDisabled="!hasInputAllowed()"
          />

          <form-button
            class="col-12 form-input"
            title="Solicitar KYC BS2"
            :loading="loading"
            :isDisabled="!hasInputAllowed() || loading"
            @clicked="onSubmitKycBs2"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  KYC_PURPOSE_OPTIONS,
  KYC_OPERATION_LEVELS,
  kycBusinessPurposeOptions,
  kycOperationalLevelOptions
} from '@shared/composables/useGlobalConfig'
import { hasInputAllowed } from '@shared/composables/useWhitelabelService.js'
import { useCache } from '@shared/stores/cache.js'
import { getBusinessPurpose } from '@shared/utils/clientHelper'
import { Collapse } from 'bootstrap'
import { formButton, formInput, formSelect } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'

export default {
  name: 'accordionKycBs2',

  components: {
    formInput,
    formSelect,
    formButton
  },

  props: {
    initialFormData: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    foreignCounterpartyName: {
      type: String,
      default: ''
    },
    foreignCounterpartyCountry: {
      type: String,
      default: ''
    }
  },

  emits: ['submit', 'update:foreignCounterpartyName', 'update:foreignCounterpartyCountry'],

  setup(props, { emit }) {
    const collapseKycPreview = ref(null)
    const isOpenedKyc = ref(false)

    const foreignCounterpartyNameModel = computed({
      get: () => props.foreignCounterpartyName,
      set: (value) => emit('update:foreignCounterpartyName', value)
    })
    const foreignCounterpartyCountryModel = computed({
      get: () => props.foreignCounterpartyCountry,
      set: (value) => emit('update:foreignCounterpartyCountry', value)
    })

    const kycBusinessPurposeValue = ref('')
    const kycOperationalLevelValue = ref('')
    const kycHowClientCapturedValue = ref('Indicação')
    const kycRelationshipTimeValue = ref('')
    const remittancePurposeValue = ref('')

    const countries = ref(
      useCache().getCountriesBanks.map((country) => {
        return { ...country, key: country.country }
      })
    )

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        kycBusinessPurpose: yup.string().required(),
        remittancePurpose: yup.string().required(),
        kycOperationalLevel: yup.string().required(),
        kycForeignCounterpartyName: yup.string().required(),
        kycForeignCounterpartyCountry: yup.string().required(),
        kycHowClientCaptured: yup.string().required(),
        kycRelationshipTime: yup.string().required()
      })
    })

    const toggleKycAccordion = () => {
      const collapseInstance = new Collapse(collapseKycPreview.value, {
        toggle: false
      })
      if (isOpenedKyc.value) {
        collapseInstance.hide()
        isOpenedKyc.value = false
      } else {
        collapseInstance.show()
        isOpenedKyc.value = true
      }
    }

    const getTargetVolumeKycOption = (targetVolume) => {
      if (targetVolume > 500_000) return KYC_OPERATION_LEVELS.ABOVE_500K
      if (targetVolume > 100_000) return KYC_OPERATION_LEVELS.BETWEEN_100K_AND_500K
      if (targetVolume > 50_000) return KYC_OPERATION_LEVELS.BETWEEN_50K_AND_100K
      return KYC_OPERATION_LEVELS.BELOW_50K
    }

    const getBusinessPurposeOption = (step1Data) => {
      if (step1Data?.demandDetails?.reason != null) {
        return KYC_PURPOSE_OPTIONS[step1Data.demandDetails.reason]
      }
    }

    const getRelationshipTime = (accounts) => {
      return accounts
        .map((account) => new Date(account.createdAt).getFullYear())
        .sort((a, b) => a - b)[0]
    }

    watch(
      () => props.initialFormData,
      (newFormData) => {
        if (newFormData == null) return
        kycRelationshipTimeValue.value = getRelationshipTime(newFormData.users)
        kycBusinessPurposeValue.value = getBusinessPurposeOption(newFormData.step1)
        remittancePurposeValue.value = getBusinessPurpose(
          newFormData.step1.demandDetails,
          newFormData.step1.demand
        )
        kycOperationalLevelValue.value = getTargetVolumeKycOption(
          newFormData.step2.pf.annualTargetVolume
        )
      },
      { immediate: true, deep: true }
    )

    const onSubmitKycBs2 = handleSubmit(() => {
      emit('submit', {
        businessPurpose: kycBusinessPurposeValue.value,
        operationalLevel: kycOperationalLevelValue.value,
        howClientCaptured: kycHowClientCapturedValue.value,
        relationshipTime: kycRelationshipTimeValue.value,
        remittancePurpose: remittancePurposeValue.value,
        foreignCounterparties: `${props.foreignCounterpartyName} - ${props.foreignCounterpartyCountry}`
      })
    })

    return {
      collapseKycPreview,
      isOpenedKyc,
      toggleKycAccordion,
      onSubmitKycBs2,
      kycBusinessPurposeValue,
      kycOperationalLevelValue,
      kycHowClientCapturedValue,
      kycRelationshipTimeValue,
      remittancePurposeValue,
      countries,
      foreignCounterpartyNameModel,
      foreignCounterpartyCountryModel,
      kycBusinessPurposeOptions,
      kycOperationalLevelOptions,
      hasInputAllowed
    }
  }
}
</script>
